import { useState } from 'react';
import { bestSellerPerfumes } from '../../../mocks/products';

const WA_BASE = 'https://wa.me/573228000501?text=Hola%2C%20me%20interesa%20el%20producto%20';

// Extra media for Set Greta (video + images)
const SET_GRETA_MEDIA: { type: 'video' | 'image'; url: string; poster?: string }[] = [
  {
    type: 'video',
    url: 'https://storage.readdy-site.link/project_files/50551f36-9f7f-4435-8b77-175c3ce034c1/7c40cead-87cc-4569-b8c7-053a566eb747_Set-Greta-1.mp4?v=68c3d232e31391129d0ebde36c65fbaa',
    poster: 'https://storage.readdy-site.link/project_files/50551f36-9f7f-4435-8b77-175c3ce034c1/2ba4f122-92b8-439f-a85e-75813ecc0188_Set-Greta-2.jpeg?v=4dca132ac959ff015ee391b84dab9497',
  },
  {
    type: 'image',
    url: 'https://storage.readdy-site.link/project_files/50551f36-9f7f-4435-8b77-175c3ce034c1/2ba4f122-92b8-439f-a85e-75813ecc0188_Set-Greta-2.jpeg?v=4dca132ac959ff015ee391b84dab9497',
  },
  {
    type: 'image',
    url: 'https://storage.readdy-site.link/project_files/50551f36-9f7f-4435-8b77-175c3ce034c1/96ed05ea-3b74-40d5-88d7-7d60c262dc48_Set-Greta-3.jpeg?v=7d3cfd80622f1de232930ccec6b61503',
  },
];

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`${i < count ? 'ri-star-fill' : 'ri-star-line'} text-amber-400`}
        />
      ))}
    </div>
  );
}

function ProductImage({ product }: { product: typeof bestSellerPerfumes[0] }) {
  const isGreta = product.name === 'Set Greta';
  const [activeIdx, setActiveIdx] = useState(0);

  if (!isGreta) {
    return (
      <div className="relative overflow-hidden aspect-[4/5]">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute top-4 left-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 whitespace-nowrap">
          <i className="ri-fire-fill" />
          Más Vendido
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
    );
  }

  const current = SET_GRETA_MEDIA[activeIdx];

  const prev = () => setActiveIdx((i) => (i - 1 + SET_GRETA_MEDIA.length) % SET_GRETA_MEDIA.length);
  const next = () => setActiveIdx((i) => (i + 1) % SET_GRETA_MEDIA.length);

  return (
    <div className="relative overflow-hidden aspect-[4/5] bg-stone-100">
      {/* Media */}
      {current.type === 'video' ? (
        <video
          key={current.url}
          src={current.url}
          poster={current.poster}
          className="w-full h-full object-cover object-top"
          autoPlay
          muted
          loop
          playsInline
        />
      ) : (
        <img
          key={current.url}
          src={current.url}
          alt={`Set Greta ${activeIdx + 1}`}
          className="w-full h-full object-cover object-top transition-opacity duration-300"
        />
      )}

      {/* Badge */}
      <div className="absolute top-4 left-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 whitespace-nowrap z-10">
        <i className="ri-fire-fill" />
        Más Vendido
      </div>

      {/* Arrow prev */}
      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-lg" />
      </button>

      {/* Arrow next */}
      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-lg" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {SET_GRETA_MEDIA.map((_, i) => (
          <button
            key={i}
            onClick={(e) => { e.stopPropagation(); setActiveIdx(i); }}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${i === activeIdx ? 'bg-white scale-125' : 'bg-white/50'}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function BestSellers() {
  return (
    <section className="py-20 px-6 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-12 h-[2px] bg-gradient-to-r from-transparent to-amber-600" />
            <i className="ri-fire-fill text-3xl text-amber-600" />
            <div className="w-12 h-[2px] bg-gradient-to-l from-transparent to-amber-600" />
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-stone-900 mb-4 tracking-tight">
            Productos Más Vendidos
          </h2>
          <p className="text-stone-600 text-lg max-w-2xl mx-auto">
            Los favoritos de nuestros clientes. Calidad y estilo que enamoran.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {bestSellerPerfumes.map((product) => (
            <div key={product.id} className="group animate-fade-in-up">
              <div className="bg-white rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                {/* Image / Carousel */}
                <ProductImage product={product} />

                {/* Content */}
                <div className="p-6 md:p-8">
                  <div className="mb-4">
                    <h3 className="text-2xl font-light text-stone-900 mb-2 tracking-tight">
                      {product.name}
                    </h3>
                    <p className="text-stone-600 text-sm mb-3 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-3xl font-light text-stone-900">{product.price}</p>
                      <div className="flex items-center gap-2">
                        <StarRating count={product.rating} />
                        <span className="text-sm text-stone-500">({product.reviews})</span>
                      </div>
                    </div>
                  </div>

                  {/* Review */}
                  {product.review && (
                    <div className="bg-stone-50 rounded-xl p-4 mb-5 border border-stone-100">
                      <div className="flex items-start gap-3">
                        <i className="ri-double-quotes-l text-2xl text-amber-600 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-stone-700 text-sm italic leading-relaxed">
                            {product.review}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <StarRating count={5} />
                            <span className="text-xs text-stone-500">- Cliente verificada</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <a
                    href={`${WA_BASE}${encodeURIComponent(product.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-gradient-to-r from-stone-900 to-stone-800 text-white py-4 rounded-xl font-medium hover:from-stone-800 hover:to-stone-700 transition-all duration-300 flex items-center justify-center gap-3 group/btn whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-fill text-2xl group-hover/btn:scale-110 transition-transform" />
                    Consultar por WhatsApp
                  </a>
                  <p className="text-center text-xs text-stone-500 mt-3">
                    Compra fácil escribiéndonos por WhatsApp
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer badge */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 bg-white px-8 py-4 rounded-full">
            <i className="ri-shield-check-fill text-2xl text-amber-600" />
            <p className="text-stone-700 font-medium">
              Productos verificados y amados por nuestros clientes
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
